import { SearchBrowseParams } from '@aibees/elephant/types';
import { useMemo } from 'react';
import { SearchFilter } from '../../..';
import { INNER_SEPARATOR, ITEM_SEPARATOR } from '../config';

/** Returns only values slice. Use when component only needs values. */
export function useEmployeeByDepartmentValuesValues(
  params: Partial<SearchBrowseParams>,
  index: number,
  field: string,
  items?: SearchFilter[]
): string[] {
  return useMemo(() => {
    if (!items?.length || !field) return [];
    const values = new Array(items.length).fill(undefined);
    const p = params[field];
    const itemValue = p?.split(ITEM_SEPARATOR);
    itemValue?.[index || 0]?.split(INNER_SEPARATOR)?.forEach((v, i) => {
      values[i] = v?.length ? v : undefined;
    });
    return values;
  }, [params, index, field, items]);
}

/** Returns only filteredItems slice. Use when component needs filteredItems (with or without values). */
export function useEmployeeByDepartmentValuesFilteredItems(
  params: Partial<SearchBrowseParams>,
  index: number,
  field: string,
  items?: SearchFilter[]
): SearchFilter[] {
  return useMemo(() => {
    if (!items?.length || !field) return [];
    const p = params[field];
    const itemValue = p?.split(ITEM_SEPARATOR);
    const keys = itemValue?.map((x) => x.split(INNER_SEPARATOR)?.[0]) || [];
    return items.map((item) => ({
      ...item,
      treeOptions: item.treeOptions?.map((x) =>
        x.filter((y) => !keys?.includes(y.value))
      ),
    }));
  }, [params, index, field, items]);
}

export function useEmployeeByDepartmentValues(
  params: Partial<SearchBrowseParams>,
  index: number,
  field: string,
  items?: SearchFilter[]
): {
  values: string[];
  keys: string[];
  filteredItems: SearchFilter[];
} {
  const values = useEmployeeByDepartmentValuesValues(
    params,
    index,
    field,
    items
  );
  const filteredItems = useEmployeeByDepartmentValuesFilteredItems(
    params,
    index,
    field,
    items
  );
  const keys = useMemo(() => {
    if (!items?.length || !field) return [];
    const p = params[field];
    const itemValue = p?.split(ITEM_SEPARATOR);
    return itemValue?.map((x) => x.split(INNER_SEPARATOR)?.[0]) || [];
  }, [params, index, field, items]);

  return useMemo(
    () => ({ values, keys, filteredItems }),
    [values, keys, filteredItems]
  );
}
